import { useState, useEffect, useCallback } from 'react';
import { getCarrierStatusHistory } from '../services/statusHistoryApi';
import type {
  StatusHistoryEvent,
  StatusHistoryQueryParams,
} from '../types/statusHistory';

interface UseCarrierStatusHistoryResult {
  /** Change events, newest first. Empty array when no history is recorded. */
  data: StatusHistoryEvent[];
  /** Total in-window events reported by the backend (before pagination). */
  total: number;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * React hook to fetch a carrier's authority/status change history.
 *
 * The not-yet-backfilled case (a carrier with no recorded changes) is a
 * NORMAL empty state: `data` is `[]`, `total` is 0, `error` is null.
 *
 * @param dotNumber - The DOT number of the carrier (null disables fetching)
 * @param params    - Optional query params (days/limit/offset/field)
 * @param autoFetch - Whether to automatically fetch on mount (default: true)
 *
 * @example
 * ```tsx
 * const { data, total, isLoading, error, refetch } = useCarrierStatusHistory(965044, { days: 90 });
 * ```
 */
export const useCarrierStatusHistory = (
  dotNumber: number | null,
  params?: StatusHistoryQueryParams,
  autoFetch: boolean = true
): UseCarrierStatusHistoryResult => {
  const [data, setData] = useState<StatusHistoryEvent[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const days = params?.days;
  const limit = params?.limit;
  const offset = params?.offset;
  const field = params?.field;

  const fetchHistory = useCallback(async () => {
    if (!dotNumber) {
      setError('No DOT number provided');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await getCarrierStatusHistory(dotNumber, {
        days,
        limit,
        offset,
        field,
      });
      setData(result.data ?? []);
      setTotal(result.total ?? 0);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load status history';
      setError(errorMessage);
      setData([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [dotNumber, days, limit, offset, field]);

  useEffect(() => {
    if (autoFetch && dotNumber) {
      fetchHistory();
    }
  }, [dotNumber, autoFetch, fetchHistory]);

  return {
    data,
    total,
    isLoading,
    error,
    refetch: fetchHistory,
  };
};

export default useCarrierStatusHistory;
